import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { FormField, Label } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { DateInput } from '../ui/date-input';
import type { CaixinhaSnapshot } from '../../types';

function toISODate(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

function fromISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

interface SnapshotFormProps {
  onSubmit: (data: { date: Date; value: number }) => Promise<void>;
  onClose?: () => void;
  initialData?: CaixinhaSnapshot | null;
}

export function SnapshotForm({ onSubmit, onClose, initialData }: SnapshotFormProps) {
  const [dateISO, setDateISO] = useState(toISODate(initialData?.date ?? new Date()));
  const [value, setValue] = useState(initialData?.value ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDateISO(toISODate(initialData?.date ?? new Date()));
    setValue(initialData?.value ?? 0);
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dateISO || value <= 0) return;
    setSaving(true);
    try {
      await onSubmit({ date: fromISODate(dateISO), value });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <FormField>
        <Label>Data</Label>
        <DateInput value={dateISO} onChange={setDateISO} />
      </FormField>

      <FormField>
        <Label>
          Valor <span className="text-destructive">*</span>
        </Label>
        <CurrencyInput value={value} onChange={setValue} />
      </FormField>

      <div className="flex gap-2">
        {onClose && (
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={value <= 0 || saving}>
          {initialData ? 'Salvar' : 'Adicionar snapshot'}
        </Button>
      </div>
    </form>
  );
}
